import React, { useState, useEffect } from 'react';
import { Package, Loader2, CheckCircle, Circle, Truck, Copy } from 'lucide-react';
import { hardwareOrdersApi } from '../utils/api';

// Hardware order tracking — read-only fulfillment progress for the client.
// Staff record the steps in EnSight; this renders the client-safe view:
// step labels, dates, and the tracking number once shipped.

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) { return iso; }
}

function fmtMoney(v) {
  if (v === null || v === undefined) return null;
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function TrackingNumber({ number, carrier }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable — number is still selectable */ }
  };
  return (
    <div style={{
      marginTop: 10, padding: '10px 14px', background: '#f0fdf4',
      border: '1px solid #bbf7d0', borderRadius: 8,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <Truck size={16} color="#16a34a" />
      <span style={{ fontSize: 13, color: '#166534' }}>
        {carrier ? `Shipped via ${carrier}` : 'Shipped'} — tracking number:
      </span>
      <code style={{ fontSize: 13, fontWeight: 700, color: '#14532d' }}>{number}</code>
      <button
        type="button"
        onClick={copy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
          border: '1px solid #bbf7d0', background: '#fff', color: '#166534',
          borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
        }}
      >
        <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function OrderCard({ order }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '18px 22px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Package size={18} color="#374151" />
          <strong style={{ fontSize: 15 }}>Hardware Order</strong>
          {order.ordered_on && (
            <span style={{ fontSize: 13, color: '#6b7280' }}>· placed {fmtDate(order.ordered_on)}</span>
          )}
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
          color: order.complete ? '#16a34a' : '#b45309',
          background: order.complete ? '#dcfce7' : '#fef3c7',
        }}>
          {order.complete ? 'Delivered to carrier' : `${order.steps_done} of ${order.steps_total} steps complete`}
        </span>
      </div>

      {(order.item_count > 0 || order.sale_total) && (
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          {order.item_count > 0 ? `${order.item_count} item${order.item_count === 1 ? '' : 's'}` : ''}
          {order.item_count > 0 && order.sale_total ? ' · ' : ''}
          {order.sale_total ? fmtMoney(order.sale_total) : ''}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {order.steps.map((step, idx) => {
          const isLast = idx === order.steps.length - 1;
          return (
            <div key={step.key} style={{ display: 'flex', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {step.done
                  ? <CheckCircle size={20} color="#16a34a" />
                  : <Circle size={20} color="#d1d5db" />}
                {!isLast && (
                  <div style={{
                    width: 2, flex: 1, minHeight: 18,
                    background: step.done ? '#86efac' : '#e5e7eb',
                  }} />
                )}
              </div>
              <div style={{ paddingBottom: isLast ? 0 : 14 }}>
                <div style={{
                  fontSize: 14, fontWeight: step.done ? 600 : 500,
                  color: step.done ? '#111827' : '#9ca3af',
                }}>
                  {step.label}
                </div>
                {step.done && step.date && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(step.date)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {order.tracking_number && (
        <TrackingNumber number={order.tracking_number} carrier={order.carrier} />
      )}
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    hardwareOrdersApi.list()
      .then(setOrders)
      .catch(() => setError('Could not load your orders. Please try again.'));
  }, []);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px' }}>
      <h1 style={{ fontSize: 22, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <Package size={22} /> Orders
      </h1>

      {error && (
        <div style={{ padding: 14, background: '#fee2e2', color: '#b91c1c', borderRadius: 8, fontSize: 14 }}>
          {error}
        </div>
      )}

      {!error && orders === null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 14 }}>
          <Loader2 size={16} className="spin" /> Loading orders…
        </div>
      )}

      {orders !== null && orders.length === 0 && (
        <div style={{ padding: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, color: '#6b7280', fontSize: 14 }}>
          No hardware orders yet. When you accept a hardware proposal, its build
          and shipping progress will appear here.
        </div>
      )}

      {orders !== null && orders.map(o => <OrderCard key={o.path_id} order={o} />)}
    </div>
  );
}
